import React, { useRef } from 'react';
import { useSpring, animated } from 'react-spring';
import styled, { css, useTheme } from 'styled-components';
import Icon from '~/components/Icon';
import JustificationContainer from '~/components/JustificationContainer';
import useSize from '~/hooks/useSize';

import { BaseColor, SystemColorPalette } from '~/theme/System/tokens';

export type Props = {
  dataTestId?: string;

  /** Group id */
  id: string;

  /** Assigns to the container to be able to scroll to the block category */
  anchorId?: string;
  header: React.ReactNode;
  variant?: keyof SystemColorPalette;
  isOpen: boolean;
  disabled?: boolean;
  itemsLength: number;
  onToggle: () => void;
  showSelectAllButton?: boolean;
  markAllCheckboxes?: () => void;
};

const CollapsibleBlock: React.FC<Props> = ({
  dataTestId,
  header,
  anchorId,
  variant = 'primary',
  children,
  isOpen,
  disabled = false,
  onToggle,
  itemsLength,
  id,
  ...rest
}) => {
  const theme = useTheme();

  const contentRef = useRef<HTMLDivElement | null>(null);

  const [{ height: contentHeight }] = useSize(contentRef);

  const { opacity } = useSpring({
    from: { opacity: 0 },
    to: { opacity: isOpen ? 1 : 0 },
    config: {
      tension: 2000,
      clamp: true,
    },
  });

  const { height, y } = useSpring({
    from: { height: 0, y: 0 },
    to: {
      height: isOpen ? contentHeight + 5 : 0,
      y: isOpen ? 0 : 20,
    },
  });

  const hasItems = itemsLength > 0;

  return (
    <Container id={anchorId} data-testid={dataTestId} {...rest} color={variant}>
      <StyledJustificationContainer
        justification="space-between"
        align="center"
        padding={isOpen ? [null, null, 'm', null] : [null]}
        data-objectid={id}
      >
        {hasItems && (
          <CollapseIcon
            name={isOpen ? 'minusSquare' : 'plusSquare'}
            strokeWidth={2.5}
            color={theme.color(variant)}
            onClick={() => onToggle && onToggle()}
          />
        )}

        {header}
      </StyledJustificationContainer>

      {children && (
        <animated.div style={{ opacity, height, overflow: 'hidden' }}>
          <ChildrenContainer
            ref={contentRef}
            style={{ y }}
            $disabled={disabled}
          >
            {children}
          </ChildrenContainer>
        </animated.div>
      )}
    </Container>
  );
};

const Container = styled.div<{ color: BaseColor }>`
  ${({ color, theme }) => css`
    margin: ${theme.space('xxxs')} 0;
    background-color: ${theme.color(color, 'translucent')};
    padding: ${theme.space('m')};
    border-radius: ${theme.getTokens().border.radius.s};

    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: 0;
    }
  `}
`;

const StyledJustificationContainer = styled(JustificationContainer)<{}>(
  ({ theme }) => css`
    user-select: none;

    ${theme.mq.lessThan('mobile')`
      flex-direction: row;
    `};
  `,
);

const ChildrenContainer = styled(animated.div)<{ $disabled: boolean }>`
  ${({ $disabled }) => css`
    opacity: ${$disabled ? 0.5 : 1};
  `}
`;

const CollapseIcon = styled(Icon)<{ disabled?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'auto' : 'pointer')};
  /** Because of use spring's initial value it is not accessible without z-index */
  z-index: 100;

  ${({ theme }) => css`
    font-size: ${theme.fs('m')};
  `};
`;

export default CollapsibleBlock;
